/**
 * @voltxampere/fx — Wire current solver (Sprint 29-2)
 *
 * Given a circuit topology + a per-component branch-current map (which
 * the apps/web layer extracts from the engine's TransientResult), assign
 * a signed current to every wire by topological lookup:
 *
 *   1. For each wire, take its netId.
 *   2. Find the first component whose terminal connects to that net.
 *   3. Use that component's branch current, signed by terminal index
 *      (pin 0 = +I, pin ≥1 = −I — Sprint 29 ships the 2-pin convention;
 *      3-pin BJT/FET sign refinement lives in Sprint 30+).
 *   4. If no component owns the net, the wire is reported as
 *      `unresolved` and its current defaults to 0.
 *
 * KCL invariant: wires that share a net carry the same magnitude. The
 * sign reflects the chosen owner's pin convention; the visual direction
 * of flow along the wire path is the renderer's concern (Sprint 29-3
 * meteor particle direction reads `WireFxState.direction` derived from
 * sign).
 *
 * IMPORT DISCIPLINE: This module touches `@voltxampere/schema` only.
 * Engine output is shuttled in via the `BranchMeasurement` interface
 * defined locally — see ADR note in `index.ts` and the 29-5 grep gate.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Compute per-wire signed current from per-component branch currents.
 *
 * Returns a `WireCurrentResult` where every wire in `circuit.wires` has
 * an entry in `currents` (defaulting to 0 for unresolved wires). The
 * `unresolved` array is empty in the typical case where every net has at
 * least one component terminal.
 */
export function solveWireCurrents(circuit, measurements) {
    const measMap = new Map(measurements.map((m) => [m.id, m.current]));
    const ownerByNet = buildNetToOwnerMap(circuit);
    const currents = new Map();
    const unresolved = [];
    for (const wire of circuit.wires) {
        const owner = ownerByNet.get(wire.netId);
        if (!owner) {
            unresolved.push(wire.id);
            currents.set(wire.id, 0);
            continue;
        }
        const branchCurrent = measMap.get(owner.componentId);
        if (branchCurrent === undefined) {
            // Owner exists in topology but engine emitted no measurement for
            // it (rare — would mean the component has no stamp wired to the
            // measurement adapter). Treat as zero rather than crashing.
            currents.set(wire.id, 0);
            continue;
        }
        const signed = signCurrentByTerminalIndex(branchCurrent, owner.terminalIndex);
        currents.set(wire.id, signed);
    }
    return { currents, unresolved };
}
/**
 * Build a netId → owner lookup. The first component terminal encountered
 * for each net wins; ties on the same net (multi-component nets) are
 * resolved deterministically by component-then-terminal traversal order
 * which mirrors the JSON serialisation of `Circuit`.
 *
 * Why first-wins is acceptable: KCL means the magnitude is identical for
 * every component on the same net; only the sign convention varies, and
 * the renderer treats sign as a visual hint (Sprint 29-3 maps
 * `direction = sign(I)` to particle flow, with zero magnitude collapsing
 * to no animation).
 */
function buildNetToOwnerMap(circuit) {
    const map = new Map();
    for (const component of circuit.components) {
        for (let i = 0; i < component.terminals.length; i++) {
            const terminal = component.terminals[i];
            if (terminal === undefined)
                continue;
            if (map.has(terminal.netId))
                continue;
            map.set(terminal.netId, { componentId: component.id, terminalIndex: i });
        }
    }
    return map;
}
/**
 * Apply the SPICE passive-sign convention adjusted for terminal index.
 *
 * - Pin 0 (canonical "+" terminal): current into pin = positive ⇒ keep sign
 * - Pin 1+ (other terminals): current out of pin = positive ⇒ flip sign
 *
 * Sprint 29 scope: 2-pin only. 3-pin BJT (B/C/E) sign refinement lives
 * in Sprint 30+ (BJT collector current convention, MOSFET drain).
 */
function signCurrentByTerminalIndex(branchCurrent, terminalIndex) {
    return terminalIndex === 0 ? branchCurrent : -branchCurrent;
}
//# sourceMappingURL=wire-current.js.map